import React, { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '@/lib/backendUrl';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { PlanModulesList } from '@/components/PlanModulesList';
import { useToast } from '@/hooks/use-toast';
import {
  AlertCircle, CheckCircle2, Copy, CreditCard, Loader2, LogOut, QrCode, RefreshCw, ShieldAlert,
} from 'lucide-react';

const API_URL = BACKEND_URL;

export default function PaywallPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { toast } = useToast();

  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [loading, setLoading] = useState(true);
  const [method, setMethod] = useState('pix'); // 'pix' | 'card'
  const [creatingPayment, setCreatingPayment] = useState(false);
  const [pixData, setPixData] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [payer, setPayer] = useState({ name: '', email: '', cpf: '' });
  const [cardForm, setCardForm] = useState({
    card_number: '', cardholder_name: '', expiration_month: '', expiration_year: '',
    security_code: '', installments: 1,
  });

  const fetchSubscription = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API_URL}/api/subscription/check`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok) setSubscription(await resp.json());
    } catch (e) { /* noop */ }
  }, []);

  const fetchPlans = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API_URL}/api/plans`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        const list = Array.isArray(data) ? data : (data.plans || []);
        setPlans(list.filter(p => (p.price || 0) > 0));
        if (list.length && !selectedPlanId) {
          const current = list.find(p => p.id === (subscription?.plan_id)) || list[0];
          setSelectedPlanId(current.id);
        }
      }
    } catch (e) { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription?.plan_id]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchSubscription();
      await fetchPlans();
      if (user?.email) setPayer(p => ({ ...p, email: user.email, name: user.name || '' }));
      setLoading(false);
    })();
  }, [fetchSubscription, fetchPlans, user?.email, user?.name]);

  // Se já destravou (pagou), redireciona
  useEffect(() => {
    if (subscription && subscription.blocked === false && subscription.reason !== 'grace') {
      const t = setTimeout(() => navigate('/dashboard', { replace: true }), 800);
      return () => clearTimeout(t);
    }
  }, [subscription, navigate]);

  const createPixPayment = async () => {
    if (!selectedPlanId) return;
    if (!payer.name || !payer.email || !payer.cpf) {
      toast({ title: 'Preencha seus dados', description: 'Nome, e-mail e CPF são obrigatórios para gerar o PIX.', variant: 'destructive' });
      return;
    }
    setCreatingPayment(true);
    setPixData(null);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API_URL}/api/payment/pix`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan_id: selectedPlanId,
          payer_name: payer.name,
          payer_email: payer.email,
          payer_cpf: payer.cpf.replace(/\D/g, ''),
        }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Erro ao gerar PIX');
      setPixData(data);
      toast({ title: 'PIX gerado!', description: 'Escaneie o QR Code ou copie o código para pagar.' });
    } catch (err) {
      toast({ title: 'Erro', description: err.message, variant: 'destructive' });
    } finally {
      setCreatingPayment(false);
    }
  };

  const createCardPayment = async () => {
    if (!selectedPlanId) return;
    if (!payer.name || !payer.email || !payer.cpf) {
      toast({ title: 'Preencha seus dados', variant: 'destructive' });
      return;
    }
    if (!cardForm.card_number || !cardForm.security_code || !cardForm.expiration_month || !cardForm.expiration_year) {
      toast({ title: 'Preencha os dados do cartão', variant: 'destructive' });
      return;
    }
    setCreatingPayment(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API_URL}/api/payment/card`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan_id: selectedPlanId,
          payer_name: payer.name,
          payer_email: payer.email,
          payer_cpf: payer.cpf.replace(/\D/g, ''),
          card_number: cardForm.card_number.replace(/\s/g, ''),
          cardholder_name: cardForm.cardholder_name || payer.name,
          expiration_month: Number(cardForm.expiration_month),
          expiration_year: Number(cardForm.expiration_year),
          security_code: cardForm.security_code,
          installments: Number(cardForm.installments) || 1,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Erro ao processar cartão');
      if (data.status === 'approved') {
        toast({ title: 'Pagamento aprovado! ✅', description: 'Redirecionando...' });
        await fetchSubscription();
      } else {
        toast({ title: 'Pagamento em análise', description: 'Assim que o processador aprovar, o acesso será liberado.' });
      }
    } catch (err) {
      toast({ title: 'Falha no pagamento', description: err.message, variant: 'destructive' });
    } finally {
      setCreatingPayment(false);
    }
  };

  const recheckPayment = async () => {
    setCheckingStatus(true);
    await fetchSubscription();
    setTimeout(() => setCheckingStatus(false), 700);
  };

  const copyPixCode = () => {
    const code = pixData?.qr_code || '';
    if (!code) return;
    navigator.clipboard.writeText(code).then(() => {
      toast({ title: 'Código copiado!', description: 'Cole no seu app do banco para pagar.' });
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-slate-500" />
      </div>
    );
  }

  const currentPlan = plans.find(p => p.id === selectedPlanId);
  const reasonLabel = {
    expired: 'Sua assinatura expirou',
    status_suspended: 'Sua assinatura foi suspensa',
    status_cancelled: 'Sua assinatura foi cancelada',
    status_blocked: 'Acesso bloqueado',
    tenant_not_found: 'Conta não encontrada',
  }[subscription?.reason] || 'Pagamento pendente';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-10 px-4" data-testid="paywall-page">
      <div className="max-w-5xl mx-auto">
        <Card className="border-red-200 bg-red-50 mb-6">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-red-100 rounded-full">
                <ShieldAlert className="w-6 h-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-red-900" data-testid="paywall-title">{reasonLabel}</h2>
                <p className="text-red-700 text-sm mt-1">
                  Para continuar usando o {subscription?.company_name || 'sistema'}, renove sua assinatura pagando via PIX ou cartão de crédito.
                  {subscription?.subscription_end && (
                    <> Seu vencimento foi em <strong>{new Date(subscription.subscription_end).toLocaleDateString('pt-BR')}</strong>.</>
                  )}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => { logout(); navigate('/login'); }} data-testid="paywall-logout-btn">
                <LogOut className="w-4 h-4 mr-2" /> Sair
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Planos */}
          <Card>
            <CardHeader>
              <CardTitle>Escolha seu plano</CardTitle>
              <CardDescription>Selecione o plano que deseja renovar</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {plans.length === 0 ? (
                <p className="text-sm text-slate-500">Nenhum plano disponível no momento.</p>
              ) : plans.map(p => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setSelectedPlanId(p.id)}
                  data-testid={`plan-${p.id}`}
                  className={`w-full text-left border rounded-lg p-4 transition ${selectedPlanId === p.id ? 'border-violet-500 bg-violet-50 ring-2 ring-violet-200' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-semibold">{p.name}</div>
                      <div className="text-2xl font-bold mt-1">R$ {Number(p.price).toFixed(2)}<span className="text-sm font-normal text-slate-500">/mês</span></div>
                      <div className="mt-2"><PlanModulesList plan={p} compact showLimits={false} /></div>
                    </div>
                    {selectedPlanId === p.id && <Badge className="bg-violet-600">Selecionado</Badge>}
                  </div>
                </button>
              ))}
            </CardContent>
          </Card>

          {/* Pagamento */}
          <Card>
            <CardHeader>
              <CardTitle>Forma de pagamento</CardTitle>
              <CardDescription>Pague com PIX (aprovação em segundos) ou cartão de crédito</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={method === 'pix' ? 'default' : 'outline'}
                  onClick={() => setMethod('pix')}
                  data-testid="method-pix-btn"
                  className={method === 'pix' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                >
                  <QrCode className="w-4 h-4 mr-2" /> PIX
                </Button>
                <Button
                  type="button"
                  variant={method === 'card' ? 'default' : 'outline'}
                  onClick={() => setMethod('card')}
                  data-testid="method-card-btn"
                >
                  <CreditCard className="w-4 h-4 mr-2" /> Cartão
                </Button>
              </div>

              <div className="grid gap-3">
                <div>
                  <Label>Nome completo</Label>
                  <Input data-testid="payer-name" value={payer.name} onChange={(e) => setPayer({ ...payer, name: e.target.value })} />
                </div>
                <div>
                  <Label>E-mail</Label>
                  <Input data-testid="payer-email" type="email" value={payer.email} onChange={(e) => setPayer({ ...payer, email: e.target.value })} />
                </div>
                <div>
                  <Label>CPF</Label>
                  <Input data-testid="payer-cpf" value={payer.cpf} onChange={(e) => setPayer({ ...payer, cpf: e.target.value })} placeholder="000.000.000-00" />
                </div>
              </div>

              {method === 'pix' && (
                <div className="space-y-3">
                  {!pixData ? (
                    <Button
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                      onClick={createPixPayment}
                      disabled={creatingPayment || !selectedPlanId}
                      data-testid="generate-pix-btn"
                    >
                      {creatingPayment ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <QrCode className="w-4 h-4 mr-2" />}
                      Gerar QR Code PIX {currentPlan && <span className="ml-1">— R$ {Number(currentPlan.price).toFixed(2)}</span>}
                    </Button>
                  ) : (
                    <div className="border rounded-lg p-4 bg-white text-center" data-testid="pix-qr-area">
                      {pixData.qr_code_base64 && (
                        <img
                          src={`data:image/png;base64,${pixData.qr_code_base64}`}
                          alt="QR Code PIX"
                          className="w-56 h-56 mx-auto"
                        />
                      )}
                      <p className="text-sm text-slate-600 mt-2">Abra o app do seu banco e escaneie ou copie o código:</p>
                      <div className="flex gap-2 mt-3">
                        <Input readOnly value={pixData.qr_code || ''} className="font-mono text-xs" data-testid="pix-copy-code" />
                        <Button type="button" variant="outline" onClick={copyPixCode} data-testid="copy-pix-btn"><Copy className="w-4 h-4" /></Button>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Button
                          type="button"
                          variant="outline"
                          className="flex-1"
                          onClick={recheckPayment}
                          disabled={checkingStatus}
                          data-testid="recheck-payment-btn"
                        >
                          {checkingStatus ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                          Já paguei — verificar
                        </Button>
                        <Button type="button" variant="ghost" onClick={() => setPixData(null)}>Gerar novo</Button>
                      </div>
                      <p className="text-xs text-slate-400 mt-2">
                        <AlertCircle className="w-3 h-3 inline mr-1" />
                        A liberação é automática em até 1 minuto após o pagamento.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {method === 'card' && (
                <div className="space-y-3">
                  <div>
                    <Label>Número do cartão</Label>
                    <Input
                      placeholder="0000 0000 0000 0000"
                      data-testid="card-number"
                      value={cardForm.card_number}
                      onChange={(e) => setCardForm({ ...cardForm, card_number: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Nome no cartão</Label>
                    <Input
                      placeholder="Como está no cartão"
                      data-testid="card-holder"
                      value={cardForm.cardholder_name}
                      onChange={(e) => setCardForm({ ...cardForm, cardholder_name: e.target.value })}
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label>Mês</Label>
                      <Input placeholder="MM" maxLength={2} data-testid="card-exp-month" value={cardForm.expiration_month} onChange={(e) => setCardForm({ ...cardForm, expiration_month: e.target.value.replace(/\D/g, '') })} />
                    </div>
                    <div>
                      <Label>Ano</Label>
                      <Input placeholder="AAAA" maxLength={4} data-testid="card-exp-year" value={cardForm.expiration_year} onChange={(e) => setCardForm({ ...cardForm, expiration_year: e.target.value.replace(/\D/g, '') })} />
                    </div>
                    <div>
                      <Label>CVV</Label>
                      <Input placeholder="000" maxLength={4} data-testid="card-cvv" value={cardForm.security_code} onChange={(e) => setCardForm({ ...cardForm, security_code: e.target.value.replace(/\D/g, '') })} />
                    </div>
                  </div>
                  <div>
                    <Label>Parcelas</Label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={cardForm.installments}
                      onChange={(e) => setCardForm({ ...cardForm, installments: Number(e.target.value) })}
                      data-testid="card-installments"
                    >
                      {[1, 2, 3, 6, 12].map(n => <option key={n} value={n}>{n}x</option>)}
                    </select>
                  </div>
                  <Button
                    className="w-full"
                    onClick={createCardPayment}
                    disabled={creatingPayment || !selectedPlanId}
                    data-testid="pay-card-btn"
                  >
                    {creatingPayment ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
                    Pagar {currentPlan && <span className="ml-1">R$ {Number(currentPlan.price).toFixed(2)}</span>}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
